import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { TextInput, Button, Text, Surface } from 'react-native-paper';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Formik } from 'formik';
import * as Yup from 'yup';
import authService from '../services/authService';
import HeaderBar from '../components/HeaderBar';
import MaxWidthContainer from '../components/MaxWidthContainer';
import { brandPrimary, brandButton } from '../theme/paperTheme';

type RootStackParamList = {
  Landing: undefined;
  Login: undefined;
  Register: undefined;
  Home: undefined;
};

type LoginScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Login'>;
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

const inputOutline = {
  outlineColor: 'rgba(15, 23, 42, 0.12)',
  activeOutlineColor: brandPrimary,
};

const LoginScreen: React.FC<LoginScreenProps> = ({ navigation }) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (values: { email: string; password: string }) => {
    try {
      setError('');
      setLoading(true);
      await authService.login(values.email, values.password);
      await new Promise((resolve) => setTimeout(resolve, 100));
      navigation.replace('Home');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to login';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 0}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <Surface style={styles.surface}>
          <View style={styles.headerWrap}>
            <HeaderBar
              buttonLabel="Register"
              onButtonPress={() => navigation.navigate('Register')}
              onLogoPress={() => navigation.navigate('Landing')}
              style={styles.headerBar}
            />
          </View>

          <MaxWidthContainer style={[styles.formContainer, isDesktop && styles.wideContainer]}>
            <View style={styles.heroSection}>
              <Text style={styles.screenLabel}>Welcome back</Text>
              <Text style={styles.title}>Sign in to Teachers Lobby</Text>
              <Text style={styles.subtitle}>
                Log in to access your account and continue where you left off.
              </Text>
            </View>

            <Surface style={styles.formCard} elevation={0}>
              {error ? <Text style={styles.error}>{error}</Text> : null}

              <Formik
                initialValues={{ email: '', password: '' }}
                validationSchema={validationSchema}
                onSubmit={handleLogin}
              >
                {({ handleChange, handleBlur, handleSubmit, values, errors, touched }) => (
                  <View>
                    <TextInput
                      label="Email"
                      value={values.email}
                      onChangeText={handleChange('email')}
                      onBlur={handleBlur('email')}
                      mode="outlined"
                      style={styles.input}
                      autoCapitalize="none"
                      keyboardType="email-address"
                      error={touched.email && !!errors.email}
                      {...inputOutline}
                    />
                    {touched.email && errors.email ? (
                      <Text style={styles.validationError}>{errors.email}</Text>
                    ) : null}

                    <TextInput
                      label="Password"
                      value={values.password}
                      onChangeText={handleChange('password')}
                      onBlur={handleBlur('password')}
                      mode="outlined"
                      style={styles.input}
                      secureTextEntry
                      error={touched.password && !!errors.password}
                      {...inputOutline}
                    />
                    {touched.password && errors.password ? (
                      <Text style={styles.validationError}>{errors.password}</Text>
                    ) : null}

                    <Button
                      mode="contained"
                      onPress={() => handleSubmit()}
                      loading={loading}
                      style={styles.primaryButton}
                      buttonColor={brandButton}
                      textColor="#ffffff"
                    >
                      Log in
                    </Button>

                    <Button
                      mode="text"
                      onPress={() => navigation.navigate('Register')}
                      style={styles.linkButton}
                      textColor={brandPrimary}
                    >
                      Don't have an account? Register
                    </Button>
                  </View>
                )}
              </Formik>
            </Surface>
          </MaxWidthContainer>
        </Surface>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  scrollContainer: {
    flexGrow: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerBar: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  formContainer: {
    maxWidth: 560,
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 48,
  },
  wideContainer: {
    maxWidth: 480,
  },
  heroSection: {
    marginBottom: 24,
  },
  screenLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
    textAlign: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 10,
    color: '#1E293B',
    letterSpacing: -0.5,
    lineHeight: 36,
  },
  subtitle: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 24,
  },
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.06,
          shadowRadius: 12,
          elevation: 3,
        }),
  },
  input: {
    marginBottom: 12,
    backgroundColor: '#FFFFFF',
  },
  primaryButton: {
    marginTop: 8,
    borderRadius: 999,
    paddingVertical: 4,
  },
  linkButton: {
    marginTop: 8,
  },
  error: {
    color: '#d32f2f',
    textAlign: 'center',
    marginBottom: 14,
    fontSize: 15,
    fontWeight: '500',
  },
  validationError: {
    color: '#d32f2f',
    fontSize: 12,
    marginTop: -6,
    marginBottom: 10,
    marginLeft: 4,
  },
});

export default LoginScreen;
